import { type PopupState, usePopupState } from 'material-ui-popup-state/hooks';
import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import { usePopoverStore } from '../stores/popover-store';

export const useManagedPopoverState = (
  state: Partial<PopupState>,
  anchorRef?: RefObject<HTMLElement>
): PopupState => {
  const popupState = usePopupState({
    ...state,
    variant: 'popover'
  });

  const registerPopover = usePopoverStore((state) => state.registerPopover);
  const unregisterPopover = usePopoverStore((state) => state.unregisterPopover);
  const setActivePopover = usePopoverStore((state) => state.setActivePopover);
  const popupId = state.popupId || 'managed-popover';

  // Keep refs to avoid stale closures
  const openRef = useRef(popupState.open);
  const closeRef = useRef(popupState.close);
  openRef.current = popupState.open;
  closeRef.current = popupState.close;

  // Register on mount
  useEffect(() => {
    registerPopover(popupId, {
      open: () => {
        if (anchorRef?.current) {
          openRef.current(anchorRef.current);
        } else {
          openRef.current();
        }
      },
      close: () => closeRef.current()
    });
    return () => unregisterPopover(popupId);
  }, [popupId, registerPopover, unregisterPopover, anchorRef]);

  // Track active popover (closes others when this one opens)
  useEffect(() => {
    if (popupState.isOpen) {
      setActivePopover(popupId);
    }
  }, [popupState.isOpen, popupId, setActivePopover]);

  return popupState;
};
